import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  login,
  loginWithGoogle,
  register,
  confirmRegistration,
  logout,
  requestPasswordReset,
  confirmPasswordReset,
  getAuthUser,
  type AuthUser,
} from '@/api/auth';

interface UseAuthReturn {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authUser = await getAuthUser();
        setUser(authUser);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        await login(email, password);
        const authUser = await getAuthUser();
        setUser(authUser);
        navigate('/app/messages');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign in failed';
        setError(message);
        throw err;
      }
    },
    [navigate],
  );

  const signInWithGoogleFn = useCallback(async () => {
    setError(null);
    try {
      await loginWithGoogle();
      // Note: This triggers a redirect to Google. The user will be redirected
      // back to the app after authentication. The session check on mount
      // (useEffect above) will pick up the authenticated state on return.
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign in failed';
      setError(message);
      throw err;
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName: string) => {
    setError(null);
    try {
      await register(email, password, displayName);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      setError(message);
      throw err;
    }
  }, []);

  const confirmSignUp = useCallback(
    async (email: string, code: string) => {
      setError(null);
      try {
        await confirmRegistration(email, code);
        navigate('/login');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Verification failed';
        setError(message);
        throw err;
      }
    },
    [navigate],
  );

  const signOut = useCallback(async () => {
    try {
      await logout();
      setUser(null);
      navigate('/login');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed';
      setError(message);
    }
  }, [navigate]);

  const forgotPassword = useCallback(async (email: string) => {
    setError(null);
    try {
      await requestPasswordReset(email);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset request failed';
      setError(message);
      throw err;
    }
  }, []);

  const resetPasswordFn = useCallback(
    async (email: string, code: string, newPassword: string) => {
      setError(null);
      try {
        await confirmPasswordReset(email, code, newPassword);
        navigate('/login');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Password reset failed';
        setError(message);
        throw err;
      }
    },
    [navigate],
  );

  const isAuthenticated = useMemo(() => !!user, [user]);

  return {
    user,
    isLoading,
    isAuthenticated,
    signIn,
    signInWithGoogle: signInWithGoogleFn,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    resetPassword: resetPasswordFn,
    error,
    clearError,
  };
}
